import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {AlertTriangle, Loader2} from 'lucide-react';
import AdminService from '@/modules/admin/services/adminService';
import type {AdminLoincCode, AdminLoincCodesResponse} from '@/shared/types/api';
import {looksLikeLoincCode} from '@mld/shared/loinc/codeShape';

/** A rule that already owns a LOINC, so the field can say so before Save does.
 *  The DB enforces this with `ux_bdr_loinc_sex`; without the warning you only
 *  learn about the clash from a 409 after filling the whole form in. */
export interface TakenCode {
    loinc: string;
    displayName: string;
    sex: string;
}

/** Below this a name search matches most of a 112k-row table and helps nobody.
 *  A code is exempt — it is exact, and short ones like `1-1` are real. */
const MIN_NAME_QUERY = 3;
const DEBOUNCE_MS = 250;

const RETIRED = new Set(['DEPRECATED', 'DISCOURAGED']);

const inputClass =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary';

/**
 * The LOINC field in the biomarker rule editor.
 *
 * It never blocks a save. Every state it can detect is a warning, because the
 * reference can legitimately lag a lab: a code absent from `loinc_codes` may be
 * real and merely not imported yet. What it will not do is let a code go in
 * silently — the whole point is that `6426-1` shows as "Histoplasma capsulatum
 * Ab" while you are typing it into a celiac rule.
 */
const LoincLookupField: React.FC<{
    value: string;
    onChange: (loinc: string) => void;
    takenCodes?: TakenCode[];
}> = ({value, onChange, takenCodes = []}) => {
    const [text, setText] = React.useState(value);
    const [debounced, setDebounced] = React.useState(value);
    const [picked, setPicked] = React.useState(false);

    // Without a dismissal path the list sits over the fields beneath it in the
    // grid, and a click aimed at "Assay code" lands on a suggestion button and
    // silently rewrites the LOINC instead.
    const [dismissed, setDismissed] = React.useState(false);
    const wrapRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setDismissed(true);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    // The parent owns the value; re-sync when it changes underneath us (the
    // editor swaps rules without remounting).
    React.useEffect(() => setText(value), [value]);

    React.useEffect(() => {
        const timer = setTimeout(() => setDebounced(text.trim()), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [text]);

    const isCode = looksLikeLoincCode(debounced);
    const enabled = debounced.length > 0 && (isCode || debounced.length >= MIN_NAME_QUERY);

    const lookup = useQuery<AdminLoincCodesResponse>({
        queryKey: ['loinc-lookup', debounced],
        // scope 'all': the in-use slice cannot answer "what is this code?" for a
        // code we have no data for, which is every code worth looking up.
        // Counts are skipped — measured at 400-900ms vs ~190ms on prod.
        queryFn: () =>
            AdminService.listLoincCodes({
                search: debounced,
                scope: 'all',
                includeUsageCounts: false,
                limit: 8,
            }),
        enabled,
        retry: false,
        staleTime: 5 * 60 * 1000,
    });

    const rows = lookup.data?.rows ?? [];
    const exact: AdminLoincCode | undefined = isCode
        ? rows.find((r) => r.loinc.toLowerCase() === debounced.toLowerCase())
        : undefined;

    const clash = takenCodes.find((t) => t.loinc === debounced);
    const showSuggestions = !isCode && enabled && !picked && !dismissed && rows.length > 0;

    const update = (next: string) => {
        setPicked(false);
        setDismissed(false);
        setText(next);
        onChange(next);
    };

    const choose = (row: AdminLoincCode) => {
        setPicked(true);
        setText(row.loinc);
        setDebounced(row.loinc);
        onChange(row.loinc);
    };

    return (
        <div
            className="relative"
            ref={wrapRef}
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    setDismissed(true);
                    e.stopPropagation(); // don't let the modal close too
                }
            }}>
            <label className="block">
                <span className="mb-1 block text-xs font-medium text-mld-text-secondary">LOINC</span>
                <input
                    aria-label="LOINC"
                    className={inputClass}
                    value={text}
                    onChange={(e) => update(e.target.value)}
                    placeholder="2085-9, or search by name"
                    autoComplete="off"
                />
            </label>

            {enabled && lookup.isFetching ? (
                <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                    <Loader2 className="h-3 w-3 animate-spin" /> Searching the LOINC reference&hellip;
                </p>
            ) : null}

            {/* The identity line. This is the control that would have caught the
                Histoplasma code, so it is plain text, not a tooltip. */}
            {exact ? (
                <p className="mt-1 text-xs text-mld-text-secondary">
                    <span className="font-medium">{exact.longName || exact.displayLabel}</span>
                    {/* A retired status gets its own warning below; repeating it
                        here would say the same word twice in two colours. */}
                    {exact.status && !RETIRED.has(exact.status.toUpperCase()) ? (
                        <span className="text-gray-500"> &middot; {exact.status}</span>
                    ) : null}
                </p>
            ) : null}

            {/* A failed lookup used to render nothing — identical to a code that
                checked out clean. That is the one thing a verification control
                must never do. */}
            {enabled && lookup.isError ? (
                <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                        This code couldn&rsquo;t be checked against the LOINC reference &mdash; the lookup failed. Treat
                        it as unverified rather than correct.
                    </span>
                </p>
            ) : null}

            {/* A name left in the box saves as the LOINC. Nothing downstream
                shape-checks it (controller parseBody takes loinc as-is), so the
                rule would store a value no result can ever match. */}
            {enabled && !isCode && !lookup.isError ? (
                <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                        <span className="font-medium">{debounced}</span> is not a LOINC code. Pick one of the matches
                        below &mdash; saving this as-is stores a code no result can match.
                    </span>
                </p>
            ) : null}

            {isCode && !lookup.isFetching && lookup.isSuccess && !exact ? (
                <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                        <span className="font-medium">{debounced}</span> is not in this environment&rsquo;s LOINC
                        reference. Either the code is wrong, or the reference has not been imported here &mdash; run{' '}
                        <code>loinc:import</code> to be sure. Saving is still allowed.
                    </span>
                </p>
            ) : null}

            {exact && exact.status && RETIRED.has(exact.status.toUpperCase()) ? (
                <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                        LOINC lists this code as <span className="font-medium">{exact.status}</span> &mdash; stop using
                        it for new data.
                    </span>
                </p>
            ) : null}

            {clash ? (
                <p className="mt-1 flex items-start gap-1 text-xs text-amber-700">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>
                        Already used by <span className="font-medium">{clash.displayName}</span> ({clash.sex}). One code
                        maps to one rule per sex, so saving will be rejected.
                    </span>
                </p>
            ) : null}

            {showSuggestions ? (
                <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-mld-panel-border bg-white shadow-lg">
                    {rows.map((row) => (
                        <li key={row.loinc}>
                            <button
                                type="button"
                                onClick={() => choose(row)}
                                className="block w-full px-3 py-2 text-left text-xs hover:bg-teal-50">
                                <span className="font-mono font-medium">{row.loinc}</span>
                                <span className="ml-2 text-mld-text-secondary">{row.longName || row.displayLabel}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

export default LoincLookupField;
